import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCRMMandates } from '../../hooks/queries';
import { areStringArraysEqual } from '../../lib/utils';
import { countriesMappedByRegion } from '../../utils/countries';
import {
  PrimaryFeature,
  SecondaryFeature,
  TertiaryFeature,
} from '../../utils/types';
import { DropdownFilter } from '../Dropdown/DropdownVariants/DropdownFilter';
import { DropdownSingleSelect } from '../Dropdown/DropdownVariants/DropdownSingleSelect';
import { DropdownOptionProps } from '../Dropdown/helper/DropdownTypes';
import { Loading } from '../Loading';
import { EditPrincipalAccessProps } from './EditPrincipalModal';

export enum CustomerDataField {
  LEADS = 'leads',
  OPPORTUNITIES = 'opportunities',
  SAMPLES = 'product-samples',
  PRODUCT_FEEDBACK = 'product-feedback',
  INCIDENTS = 'incidents',
}

export type CustomerDataByReportType = {
  [key in CustomerDataField]: string[];
};

type FeatureKey = ['disabled', 'allowed', 'maintenance'];

type FeatureStatuses = {
  [key in FeatureKey[number]]: string[];
};

type FeatureStatus = {
  [featureId: string]: 'allowed' | 'disabled' | 'maintenance';
};

type ModalStructure = {
  primaryFeature: {
    primaryFeatureId: PrimaryFeature;
    secondaryFeature: {
      secondaryFeatureId: SecondaryFeature;
      customerDataField?: CustomerDataField;
      tertiaryFeature?: {
        tertiaryFeatureId: TertiaryFeature;
      }[];
    }[];
  };
}[];

const ModalStructure: ModalStructure = [
  {
    primaryFeature: {
      primaryFeatureId: PrimaryFeature.DASHBOARD,
      secondaryFeature: [
        {
          secondaryFeatureId: SecondaryFeature.DASHBOARD_KEY_SALES_NUMBERS,
        },
      ],
    },
  },
  {
    primaryFeature: {
      primaryFeatureId: PrimaryFeature.REPORTING,
      secondaryFeature: [
        {
          secondaryFeatureId: SecondaryFeature.REPORTING_SALES,
        },
        {
          secondaryFeatureId: SecondaryFeature.REPORTING_TARGETS,
        },
        {
          secondaryFeatureId: SecondaryFeature.REPORTING_OPPORTUNITIES,
          customerDataField: CustomerDataField.OPPORTUNITIES,
        },
        {
          secondaryFeatureId: SecondaryFeature.REPORTING_LEADS,
          customerDataField: CustomerDataField.LEADS,
        },
        {
          secondaryFeatureId: SecondaryFeature.REPORTING_PRODUCT_SAMPLES,
          customerDataField: CustomerDataField.SAMPLES,
        },
        {
          secondaryFeatureId: SecondaryFeature.REPORTING_SUPPLY_CHAIN,
        },
        {
          secondaryFeatureId: SecondaryFeature.REPORTING_INCIDENTS,
          customerDataField: CustomerDataField.INCIDENTS,
        },
      ],
    },
  },
  {
    primaryFeature: {
      primaryFeatureId: PrimaryFeature.LABS_AND_FORMULATIONS,
      secondaryFeature: [
        {
          secondaryFeatureId:
            SecondaryFeature.LABS_AND_FORMULATIONS_FORMULATIONS,
        },
      ],
    },
  },
  {
    primaryFeature: {
      primaryFeatureId: PrimaryFeature.INSIGHTS,
      secondaryFeature: [
        {
          secondaryFeatureId: SecondaryFeature.INSIGHTS_MARKET_INSIGHTS,
        },
        {
          secondaryFeatureId: SecondaryFeature.INSIGHTS_PRODUCT_FEEDBACK,
          customerDataField: CustomerDataField.PRODUCT_FEEDBACK,
        },
        {
          secondaryFeatureId:
            SecondaryFeature.INSIGHTS_CUSTOMER_PORTAL_INSIGHTS,
          tertiaryFeature: [
            {
              tertiaryFeatureId:
                TertiaryFeature.INSIGHTS_CUSTOMER_PORTAL_INSIGHTS_PRODUCT_VIEWS,
            },
            {
              tertiaryFeatureId:
                TertiaryFeature.INSIGHTS_CUSTOMER_PORTAL_INSIGHTS_MY_SUPPLIER_PAGE,
            },
          ],
        },
        {
          secondaryFeatureId: SecondaryFeature.INSIGHTS_SOCIAL_LISTENING,
        },
        {
          secondaryFeatureId: SecondaryFeature.INSIGHTS_EXPANSION_OPPORTUNITIES,
        },
      ],
    },
  },
  {
    primaryFeature: {
      primaryFeatureId: PrimaryFeature.COLLABORATION,
      secondaryFeature: [
        {
          secondaryFeatureId: SecondaryFeature.COLLABORATION_SHARED_DOCUMENTS,
        },
        {
          secondaryFeatureId: SecondaryFeature.COLLABORATION_API_MANAGEMENT,
        },
        {
          secondaryFeatureId: SecondaryFeature.COLLABORATION_EDI_INFORMATION,
        },
      ],
    },
  },
];

export const EditDataAccess: React.FC<EditPrincipalAccessProps> = ({
  principalInformation,
  onChange,
}) => {
  const { t } = useTranslation('translation');

  const { data: mandatesData } = useCRMMandates({
    principalPri: principalInformation?.id ?? null,
    portfolioLevel: principalInformation?.portfolioLevel,
  });

  const editDataAccessModalOptions: {
    name: string;
    value: keyof FeatureStatuses;
  }[] = [
    {
      name: t(
        'features:admin-center:principalAdministration:permissionOptions:disabled',
      ),
      value: 'disabled',
    },
    {
      name: t(
        'features:admin-center:principalAdministration:permissionOptions:allowed',
      ),
      value: 'allowed',
    },
    {
      name: t(
        'features:admin-center:principalAdministration:permissionOptions:maintenance',
      ),
      value: 'maintenance',
    },
  ];

  const [featureStatus, setFeatureStatus] = useState<FeatureStatus | null>(
    null,
  );

  const [customerData, setCustomerData] =
    useState<CustomerDataByReportType | null>(null);

  useEffect(() => {
    if (!principalInformation) return;
    const initializeFeatureStatusToAllowed: FeatureStatus = [
      ...Object.values(SecondaryFeature),
      ...Object.values(TertiaryFeature),
    ].reduce((acc: FeatureStatus, curr) => {
      acc[curr] = 'allowed';
      return acc;
    }, {});
    principalInformation.disabledFeatures?.forEach((disabledFeatureId) => {
      initializeFeatureStatusToAllowed[disabledFeatureId] = 'disabled';
    });
    principalInformation.maintenanceMode?.forEach((maintenanceFeatureId) => {
      initializeFeatureStatusToAllowed[maintenanceFeatureId] = 'maintenance';
    });

    setFeatureStatus({
      ...initializeFeatureStatusToAllowed,
    });

    const newCustomerData: CustomerDataByReportType = {
      leads: principalInformation.customerData?.leads ?? [],
      opportunities: principalInformation.customerData?.opportunities ?? [],
      ['product-samples']:
        principalInformation.customerData?.['product-samples'] ?? [],
      ['product-feedback']:
        principalInformation.customerData?.['product-feedback'] ?? [],
      incidents: principalInformation.customerData?.incidents ?? [],
    };
    setCustomerData({ ...newCustomerData });
  }, [principalInformation]);

  const onDataAccessChangeHandler = (
    featureId: string,
    selectedOption: FeatureKey[number],
  ): void => {
    if (featureStatus?.[featureId] === selectedOption) return;

    const updatedFeatureStatus = {
      ...featureStatus,
      [featureId]: selectedOption,
    };
    if (!customerData) return;
    setFeatureStatus(updatedFeatureStatus);
    updatePrincipalInformation(updatedFeatureStatus, customerData);
  };

  const onCustomerDataChangeHandler = (
    customerDataField: CustomerDataField,
    selectedCountries: string[],
  ): void => {
    if (
      !customerData ||
      areStringArraysEqual(customerData[customerDataField], selectedCountries)
    )
      return;

    const updatedCustomerData = {
      ...customerData,
      [customerDataField]: selectedCountries,
    };
    if (!featureStatus) return;
    setCustomerData(updatedCustomerData);

    updatePrincipalInformation(featureStatus, updatedCustomerData);
  };

  const resolveDropdownState = (featureId: string) => {
    if (!featureStatus) return;
    if (featureStatus[featureId] === 'disabled')
      return editDataAccessModalOptions[0];
    if (featureStatus[featureId] === 'maintenance')
      return editDataAccessModalOptions[2];

    return editDataAccessModalOptions[1];
  };

  const calculateUpdatedPrincipalInformation = (
    featureStatus: FeatureStatus,
  ): {
    disabledFeatures: string[];
    maintenanceFeatures: string[];
  } => {
    let disabledFeatures = Object.keys(featureStatus).filter(
      (key) => featureStatus[key] === 'disabled',
    );
    const maintenanceFeatures = Object.keys(featureStatus).filter(
      (key) => featureStatus[key] === 'maintenance',
    );

    ModalStructure.forEach(({ primaryFeature }) => {
      const { primaryFeatureId, secondaryFeature } = primaryFeature;

      secondaryFeature.forEach(({ secondaryFeatureId, tertiaryFeature }) => {
        if (tertiaryFeature) {
          const allTertiaryDisabled = tertiaryFeature.every(
            ({ tertiaryFeatureId }) =>
              disabledFeatures.includes(tertiaryFeatureId),
          );
          if (allTertiaryDisabled) {
            if (!disabledFeatures.includes(secondaryFeatureId)) {
              disabledFeatures.push(secondaryFeatureId);
            }
          } else {
            disabledFeatures = disabledFeatures.filter(
              (id) => id !== secondaryFeatureId,
            );
          }
        }
      });

      const allSecondariesDisabled = secondaryFeature.every(
        ({ secondaryFeatureId }) =>
          disabledFeatures.includes(secondaryFeatureId),
      );
      if (allSecondariesDisabled) {
        if (!disabledFeatures.includes(primaryFeatureId)) {
          disabledFeatures.push(primaryFeatureId);
        }
      } else {
        disabledFeatures = disabledFeatures.filter(
          (id) => id !== primaryFeatureId,
        );
      }
    });

    return { disabledFeatures, maintenanceFeatures };
  };

  const updatePrincipalInformation = (
    updatedFeatureStatus: FeatureStatus,
    updatedCustomerData: CustomerDataByReportType,
  ) => {
    const { disabledFeatures, maintenanceFeatures } =
      calculateUpdatedPrincipalInformation(updatedFeatureStatus);

    if (!principalInformation) return;
    onChange({
      ...principalInformation,
      disabledFeatures,
      maintenanceMode: maintenanceFeatures,
      customerData: updatedCustomerData,
    });
  };

  const fullAvailableCountries = useMemo(
    () =>
      mandatesData?.countries.map((country) => ({
        ISO_A3: country.iso,
        name: country.name,
      })) ?? [],
    [mandatesData?.countries],
  );

  const regions = countriesMappedByRegion(fullAvailableCountries);
  const availableCountries: DropdownOptionProps[] = Object.entries(regions).map(
    ([region, countries]) => {
      return {
        name: region,
        value: region,
        childOptions: countries.map(({ ISO_A3, name }) => ({
          name,
          value: ISO_A3,
        })),
      };
    },
  );

  const isLoading = !featureStatus || !availableCountries.length;

  return (
    <>
      <p className="font-regular text-grey-700 mb-6 pt-1 text-left text-base leading-4">
        {t(
          `features:admin-center:principalAdministration:editPrincipalModal:data-access:description`,
        )}
      </p>
      <Loading loading={isLoading}>
        {!!featureStatus &&
          ModalStructure.map(({ primaryFeature }) => {
            const primaryFeatureId = primaryFeature.primaryFeatureId;
            return (
              <React.Fragment key={primaryFeatureId}>
                <div className="mb-4">
                  <hr className="h-1px bg-grey-300 mx-auto w-full rounded border-0" />
                  <div className="flex justify-between">
                    <div className="text-grey-700 my-2 content-center text-base font-semibold leading-4">
                      {t(
                        `features:admin-center:principalAdministration:editPrincipalModal:data-access:optionSections:${primaryFeatureId}`,
                      )}
                    </div>
                  </div>
                  <hr className="h-1px bg-grey-300 mx-auto w-full rounded border-0" />
                </div>
                {primaryFeature.secondaryFeature?.map(
                  (
                    { secondaryFeatureId, customerDataField, tertiaryFeature },
                    indexNum,
                    array,
                  ) => (
                    <React.Fragment key={secondaryFeatureId}>
                      <div className="pl-4">
                        <div className="flex justify-between">
                          <div className="text-grey-700 font-regular content-center text-base leading-4">
                            {t(
                              `features:admin-center:principalAdministration:editPrincipalModal:data-access:optionSections:${secondaryFeatureId}`,
                            )}
                          </div>
                          <div className=" text-grey-700 w-240px mb-2 mt-2">
                            {tertiaryFeature ? (
                              <div className="font-regular py-2 pr-4 text-xs">
                                {t(
                                  'features:admin-center:principalAdministration:editPrincipalModal:data-access:accessIsDefinedForSubpages',
                                )}
                              </div>
                            ) : (
                              <DropdownSingleSelect
                                initialSelection={resolveDropdownState(
                                  secondaryFeatureId,
                                )}
                                onChange={(option) =>
                                  onDataAccessChangeHandler(
                                    secondaryFeatureId,
                                    option.value as FeatureKey[number],
                                  )
                                }
                                options={editDataAccessModalOptions}
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {!!customerDataField && (
                        <React.Fragment>
                          <div
                            className={classNames('pl-7', {
                              'text-grey-400':
                                featureStatus[secondaryFeatureId] !== 'allowed',
                              'text-grey-700':
                                featureStatus[secondaryFeatureId] === 'allowed',
                            })}
                          >
                            <div
                              className={classNames(
                                'font-regular mb-3 mt-3 content-center text-base',
                              )}
                            >
                              {t(
                                'features:admin-center:principalAdministration:editPrincipalModal:data-access:optionSections:includeCustomerData',
                              )}
                            </div>
                            <div className="flex justify-between">
                              <div className="font-regular w-[300px] text-xs">
                                {t(
                                  'features:admin-center:principalAdministration:editPrincipalModal:data-access:optionSections:includeCustomerDataDescription',
                                  {
                                    secondaryFeature: t(
                                      `features:admin-center:principalAdministration:editPrincipalModal:data-access:optionSections:${secondaryFeatureId}`,
                                    ),
                                  },
                                )}
                              </div>
                              <div className="w-240px mb-3">
                                <DropdownFilter
                                  typeAhead={true}
                                  disabled={
                                    featureStatus[secondaryFeatureId] !==
                                    'allowed'
                                  }
                                  label={t(
                                    'features:reporting:filters:country',
                                  )}
                                  selectAllLabel={t('placeholders:selectAll')}
                                  deselectAllLabel={t(
                                    'placeholders:deselectAll',
                                  )}
                                  options={availableCountries}
                                  initialSelection={customerData?.[
                                    customerDataField
                                  ].map((countryIso) => ({
                                    name:
                                      fullAvailableCountries.find(
                                        ({ ISO_A3 }) => ISO_A3 === countryIso,
                                      )?.name ??
                                      t(
                                        'features:admin-center:principalAdministration:editPrincipalModal:data-access:optionSections:unknownCountryIso',
                                      ),
                                    value: countryIso,
                                  }))}
                                  onChange={(option) =>
                                    onCustomerDataChangeHandler(
                                      customerDataField,
                                      option.map((option) =>
                                        option.value.toString(),
                                      ),
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </React.Fragment>
                      )}
                      {indexNum !== array.length - 1 && (
                        <div className="pl-4">
                          <hr className="h-1px bg-grey-300 mx-auto w-full rounded border-0" />
                        </div>
                      )}
                      {tertiaryFeature && (
                        <React.Fragment>
                          {tertiaryFeature?.map(
                            ({ tertiaryFeatureId }, indexNum, array) => (
                              <React.Fragment key={tertiaryFeatureId}>
                                <div className="pl-7">
                                  <div className="flex justify-between">
                                    <div className="text-grey-700 font-regular content-center text-base leading-4">
                                      {t(
                                        `features:admin-center:principalAdministration:editPrincipalModal:data-access:optionSections:${tertiaryFeatureId}`,
                                      )}
                                    </div>
                                    <div className="w-240px mb-2 mt-2">
                                      <DropdownSingleSelect
                                        initialSelection={resolveDropdownState(
                                          tertiaryFeatureId,
                                        )}
                                        onChange={(option) =>
                                          onDataAccessChangeHandler(
                                            tertiaryFeatureId,
                                            option.value as FeatureKey[number],
                                          )
                                        }
                                        options={editDataAccessModalOptions}
                                      />
                                    </div>
                                  </div>
                                  {indexNum !== array.length - 1 && (
                                    <hr className="h-1px bg-grey-300 mx-auto w-full rounded border-0" />
                                  )}
                                </div>
                              </React.Fragment>
                            ),
                          )}
                          <div className="pl-4">
                            <hr className="h-1px bg-grey-300 mx-auto w-full rounded border-0" />
                          </div>
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  ),
                )}
              </React.Fragment>
            );
          })}
      </Loading>
    </>
  );
};
